import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trimmingService } from "../services/trimmingService";
import api from "../services/api";

const TrimmingApplications = () => {
  const location = useLocation();
  // Extract status from path: /admin/trimming/total -> total
  const pathParts = location.pathname.split("/");
  const status = pathParts[pathParts.length - 1]; // total, approved, pending, rejected
  const [trimmings, setTrimmings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadTrimmings();
  }, [status, currentPage]);

  const loadTrimmings = async () => {
    setLoading(true);
    try {
      let response;
      if (status === "total") {
        response = await api.get("/userdashboard/trimming/total", {
          params: { page: currentPage, limit: 20 },
        });
      } else if (status === "approved" || status === "approve") {
        response = await api.get("/userdashboard/trimming/approve", {
          params: { page: currentPage, pageSize: 20 },
        });
      } else if (status === "pending") {
        response = await api.get("/userdashboard/trimming/pending", {
          params: { page: currentPage, pageSize: 20 },
        });
      } else if (status === "rejected") {
        response = await api.get("/userdashboard/trimming/rejected", {
          params: { page: currentPage, pageSize: 20 },
        });
      } else {
        // Default: all trimmings
        response = await trimmingService.getAllTrimmings(currentPage, 20);
      }

      if (response.data) {
        if (response.data.data) {
          setTrimmings(response.data.data);
        }
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else if (response.data.totalPages) {
          setPagination({
            page: response.data.currentPage || currentPage,
            limit: response.data.pageSize || 20,
            total: response.data.totalRecords || 0,
            totalPages: response.data.totalPages || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error loading trimmings:", error);
      alert("Error loading applications: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "total":
        return "Total Applications";
      case "approved":
      case "approve":
        return "Approved Applications";
      case "pending":
        return "Pending Applications";
      case "rejected":
        return "Rejected Applications";
      default:
        return "Trimming Applications";
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">{getStatusTitle()}</h2>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Sr. No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Permission ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Division
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Applied On
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trimmings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  trimmings.map((trimming, index) => (
                    <tr key={trimming.permission_id || trimming.id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * 20 + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {trimming.permission_id || trimming.id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.name || trimming.user_name || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.division || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            (trimming.status || trimming.permission_status) === "Approved" ||
                            (trimming.status || trimming.permission_status) === "approved"
                              ? "bg-blue-100 text-blue-800"
                              : (trimming.status || trimming.permission_status) === "Rejected" ||
                                (trimming.status || trimming.permission_status) === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {trimming.status || trimming.permission_status || "Raised"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.created_at
                          ? new Date(trimming.created_at).toLocaleDateString("en-GB")
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(currentPage - 1) * 20 + 1} to{" "}
                {Math.min(currentPage * 20, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))
                  }
                  disabled={currentPage >= pagination.totalPages}
                  className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrimmingApplications;

